import { z } from 'zod'

import { bsDateSchema, moneySchema } from '@/schemas/common'

/**
 * The NEPSE index card and the ticker tape.
 *
 * Every decimal here is `Money` — the index value, the point change and the
 * percentage alike. A percentage is a decimal shown with `formatPercent`, so
 * treating it as a number would reintroduce the float rounding the brand
 * exists to prevent. Only counts (rose/fell/flat) and chart coordinates are
 * numbers.
 */

/**
 * Every timeframe an index chart can ask for. Home's card draws ● LIVE 1D 1W
 * 1M 1Y ALL; Market Overview's V2 panel (`4236:86804`) draws 1D 1W 1M 3M 1Y
 * ALL. Each surface picks its own chips from this list — this is the union,
 * not a chip row. `3m` maps to the history endpoint's `3M`, which
 * `/api/v5/market/indices/{key}/history/` accepts.
 */
export const INDEX_RANGES = ['live', '1d', '1w', '1m', '3m', '1y', 'all'] as const

export const indexRangeSchema = z.enum(INDEX_RANGES)

/**
 * One coordinate of the index series.
 *
 * `time` is epoch seconds and `value` a plain number because these feed a
 * charting library and nothing else — they are never summed, compared or
 * displayed as a figure. Anything a member reads as a number comes from a
 * `Money` field on the summary instead.
 */
export const indexPointSchema = z.object({
  time: z.number().int(),
  value: z.number(),
})

/**
 * One bar of the same series, when the wire carries a whole session per point
 * rather than a single close.
 *
 * Numbers, not Money, for exactly the reason `indexPointSchema` above gives:
 * these are chart coordinates and nothing else reads them. `volume` is
 * optional because the histogram under the candles is optional — a series that
 * reports no volume draws candles and no bars, rather than a row of zeroes
 * that would read as "nothing traded".
 *
 * Filled from `/api/v5/charts/history/` — the TradingView UDF datafeed — or,
 * for an index, from the session each `/api/v5/market/indices/{key}/history/`
 * point carries. `marketMappers.barCandles` and `chartCandles` do the two
 * mappings.
 */
export const candlePointSchema = z.object({
  time: z.number().int(),
  open: z.number(),
  high: z.number(),
  low: z.number(),
  close: z.number(),
  volume: z.number().optional(),
})

export const marketBreadthSchema = z.object({
  rose: z.number().int(),
  fell: z.number().int(),
  flat: z.number().int(),
})

export const indexSummarySchema = z.object({
  /** The wire's display name for this index (`IndexQuote.name`) — e.g. "NEPAL
   *  STOCK EXCHANGE" for the default NEPSE row. `indexKey` below is the short
   *  code the same row's `key` carries. */
  index: z.string().min(1),
  /** The wire's own short code ("NEPSE", not "NEPAL STOCK EXCHANGE") —
   *  `IndexQuote.key`, the same string `useIndexSummary`'s `indexKey`
   *  argument matches against. A caller that wants the short form reads this
   *  instead of hand-maintaining a name→abbreviation table that mm-be's own
   *  display names would eventually drift out of sync with. */
  indexKey: z.string().min(1),
  value: moneySchema,
  /** `null` when mm-be reports no rupee point change for this index. */
  pointChange: moneySchema.nullable(),
  percentChange: moneySchema,
  asOf: z.object({
    date: bsDateSchema,
    time: z.string().regex(/^\d{1,2}:\d{2} [AP]M$/, 'Expected a clock time, e.g. "3:00 PM"'),
  }),
  isOpen: z.boolean(),
  /** Rupees in crore, as the card renders it: "Turnover Rs 507.35 Cr". `null`
   *  when the session summary carries no turnover figure. */
  turnoverCr: moneySchema.nullable(),
  /** Kitta in crore: "Volume 1.84 Cr kitta". `null` when unreported. */
  volumeCrKitta: moneySchema.nullable(),
  breadth: marketBreadthSchema,
  points: z.array(indexPointSchema),
  /**
   * The same range as `points`, as bars — ABSENT, not empty, when the response
   * carried no open/high/low/close. Optional rather than nullable so the
   * distinction stays one concept ("there are no candles for this series")
   * instead of two the UI would have to tell apart; `resolveChartView` treats
   * an empty array the same way for the same reason.
   */
  candles: z.array(candlePointSchema).optional(),
})

/**
 * One chip on the tape. `value` and `percentChange` are independently absent:
 * NEPSE carries both, a sector carries only a change, and "Turnover Rs 8.2
 * Arba" is a label with neither — so the label is the only required text and
 * a component must not assume a figure is there to render.
 */
export const tickerItemSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  percentChange: moneySchema.nullable(),
  value: moneySchema.nullable(),
})

export const tickerTapeSchema = z.object({
  status: z.enum(['closed', 'open']),
  items: z.array(tickerItemSchema),
})

/**
 * One row of the Market indices table: NEPSE, Sensitive, Float, Sensitive
 * float.
 *
 * `id` is the kebab slug the chart above the table switches on ("nepse",
 * "sensitive"), so a clicked row and a `useIndexSummary` range never have to
 * agree on a display name.
 *
 * `high52`/`low52` are the index row's own `fifty_two_week_high`/`_low` from
 * `/api/v5/market/indices/`. The wire types both nullable, so each is `null`
 * when mm-be has no bound for that index — a value-level gap the table shows as
 * "Not available yet", not a reason to drop the row or the other bound.
 */
export const indexRowSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  /** `core` for the four NEPSE-wide readings, `sector` for a sector's own index. */
  group: z.enum(['core', 'sector']),
  close: moneySchema,
  percentChange: moneySchema,
  high52: moneySchema.nullable(),
  low52: moneySchema.nullable(),
})

export const indicesPageSchema = z.object({
  rows: z.array(indexRowSchema),
  mitraSays: z.string().min(1),
})

/**
 * The market mood: mm-be's Fear & Greed composite, today's reading (`features/market/lib/mood.ts`).
 * `score` is a coordinate as much as a figure — the gauge's needle angle — so a number, 0 to 100.
 * `band` is the band mm-be named for it; `extreme` asks for the greed mascot's extreme pose.
 * `date` is the session it reads, as mm-be dates it.
 */
export const marketMoodSchema = z.object({
  score: z.number().min(0).max(100),
  band: z.enum(['fear', 'cautious', 'neutral', 'warm', 'greed']),
  extreme: z.boolean(),
  date: z.string().min(1),
})

/** One session of the mood's history, for the trend bars. */
export const moodDaySchema = z.object({
  date: z.string().min(1),
  score: z.number().min(0).max(100),
  band: z.enum(['fear', 'cautious', 'neutral', 'warm', 'greed']),
})

/**
 * Today's session as intraday points (the PROPOSED `/market/indices/{key}/intraday/`,
 * `mm-homex-2026-10-28/asks/nepse.md`). Empty when the answer holds no intraday series — never
 * filled with daily points, which a 1D chart must not draw.
 */
export const indexIntradaySchema = z.object({
  points: z.array(indexPointSchema),
})

export type IndexRange = z.infer<typeof indexRangeSchema>
export type IndexPoint = z.infer<typeof indexPointSchema>
export type CandlePoint = z.infer<typeof candlePointSchema>
export type MarketBreadth = z.infer<typeof marketBreadthSchema>
export type IndexSummary = z.infer<typeof indexSummarySchema>
export type MarketMood = z.infer<typeof marketMoodSchema>
export type MoodDay = z.infer<typeof moodDaySchema>
export type IndexIntraday = z.infer<typeof indexIntradaySchema>
export type TickerItem = z.infer<typeof tickerItemSchema>
export type TickerTape = z.infer<typeof tickerTapeSchema>
export type IndexRow = z.infer<typeof indexRowSchema>
export type IndicesPage = z.infer<typeof indicesPageSchema>
